import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { LoggerService } from '@core/services/logger.service';
import { ProcessMonitorService } from '@core/services/process-monitor.service';
import { Button } from 'primeng/button';
import { Divider } from 'primeng/divider';
import { Menu } from 'primeng/menu';
import { OverlayBadge } from 'primeng/overlaybadge';

@Component({
  selector: 'fl-process-notifier',
  imports: [Menu, Button, OverlayBadge, Divider],
  animations: [
    trigger('slideInRight', [
      transition(':enter', [
        style({ transform: 'translateX(16px)', opacity: 0 }),
        animate('180ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate('150ms ease-in', style({ transform: 'translateX(16px)', opacity: 0 })),
      ]),
    ]),
  ],
  template: `
    @if (processes().length > 0) {
      <div class="flex items-center" [@slideInRight]="true">
        <p-button
          text
          size="large"
          (onClick)="menu.toggle($event)"
          aria-label="Processi in background">
          <ng-template #content>
            <p-overlaybadge
              [value]="active()"
              [badgeDisabled]="active() === 0"
              [severity]="hasOnlyError() ? 'danger' : 'success'">
              <i
                [class]="
                  hasRunning()
                    ? 'pi pi-spin pi-cog text-2xl text-color'
                    : hasOnlyError()
                      ? 'pi pi-times-circle text-2xl text-red-500'
                      : 'pi pi-check-circle text-2xl text-green-500'
                "></i>
            </p-overlaybadge>
          </ng-template>
        </p-button>
        <p-menu #menu [popup]="true" [model]="menuItems()" appendTo="body" styleClass="max-w-md">
          <ng-template #item let-item>
            <div class="flex items-start justify-between gap-2 min-w-0 p-4">
              <i [class]="item.icon" class="mr-2"></i>
              <span class="break-words whitespace-normal block flex-1 min-w-0">{{
                item.label
              }}</span>
              @if (item.status === 'error' || item.status === 'completed') {
                <p-button
                  icon="pi pi-times"
                  size="small"
                  severity="secondary"
                  aria-label="Rimuovi processo"
                  (onClick)="remove(item.id)"></p-button>
              }
            </div>
          </ng-template>
          @if (processes().length > 1 && active() === 0) {
            <ng-template #end>
              <div
                class="flex items-center justify-between p-3  border-b border-surface-200 bg-surface-50">
                <span class="text-sm font-medium text-color"
                  >Processi: {{ processes().length }}</span
                >
                <p-button
                  icon="pi pi-trash"
                  aria-label="Elimina tutte le notifiche"
                  severity="danger"
                  size="small"
                  [outlined]="true"
                  label="Rimuovi tutti"
                  (onClick)="removeAll()"></p-button>
              </div>
            </ng-template>
          }
          <ng-template pTemplate="empty">
            <span>Nessun processo in corso</span>
          </ng-template>
        </p-menu>
        <p-divider layout="vertical" class="mx-0 mr-2"></p-divider>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [],
})
export class ProcessNotifierComponent {
  private readonly monitor = inject(ProcessMonitorService);
  private readonly logger = inject(LoggerService);

  readonly active = computed(
    () => this.monitor.processes().filter(p => p.status === 'running').length
  );

  readonly processes = this.monitor.processes;
  readonly hasRunning = () => this.monitor.hasRunning();
  readonly hasOnlyError = () => this.monitor.hasOnlyError();
  readonly remove = (id: string) => {
    this.logger.info(`[ProcessNotifier] Rimozione processo`, id);
    this.monitor.remove(id);
  };

  /**
   * Rimuove tutti i processi.
   */
  readonly removeAll = () => {
    this.logger.info(`[ProcessNotifier] Rimozione di tutti i processi`);
    this.processes().forEach(p => this.monitor.remove(p.id));
  };
  readonly menuItems = () =>
    this.processes().map(process => ({
      id: process.id,
      label: process.label,
      icon:
        process.status === 'error'
          ? 'pi pi-times-circle text-red-500'
          : process.status === 'running'
            ? 'pi pi-spin pi-spinner'
            : 'pi pi-check-circle text-green-500',
      status: process.status,
      command: () => {
        this.remove(process.id);
      },
    }));
}
